import React, { useState } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import { Drawer } from '../../../../components/drawers';
import toast from '../../../../utils/toast';
import BlacklistedMobileNumberFormFields from './BlacklistedMobileNumberFormFields';
import { BlacklistedMobileNumberItem } from '../hooks/useBlacklistedMobileNumbers';

interface AddBlacklistedMobileNumberFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newItem: BlacklistedMobileNumberItem) => void;
}

interface FormData {
  mobile_number: string;
  reason: string;
  active: boolean;
}

const AddBlacklistedMobileNumberForm: React.FC<AddBlacklistedMobileNumberFormProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<FormData>({
    mobile_number: '',
    reason: '',
    active: true
  });
  const [loading, setLoading] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const handleClose = () => {
    setFormData({ mobile_number: '', reason: '', active: true });
    setIsPhoneValid(false);
    onClose();
  };

  const handleMobileNumberChange = (value: string) => {
    setFormData(prev => ({ ...prev, mobile_number: value }));
  };

  const handlePhoneValidationChange = (isValid: boolean) => {
    setIsPhoneValid(isValid);
  };

  const handleReasonChange = (value: string) => {
    setFormData(prev => ({ ...prev, reason: value }));
  };

  const handleActiveChange = (value: boolean) => {
    setFormData(prev => ({ ...prev, active: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post<BlacklistedMobileNumberItem>(`${environment.baseUrl}/blacklisted-mobile-numbers/`, formData);
      toast.success('Mobile number blacklisted successfully');
      onSuccess(response.data);
      handleClose();
    } catch (err: any) {
      if (err.response?.status === 409) {
        toast.error('This mobile number has already been blacklisted');
      } else if (err.response?.status === 400) {
        toast.error('Invalid mobile number format or reason too long');
      } else {
        toast.error(err.response?.data?.message || 'Failed to blacklist mobile number');
      }
    } finally {
      setLoading(false);
    }
  };

  const isValid = formData.mobile_number.trim().length > 0 && isPhoneValid;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Drawer.Header title="Add Blacklisted Mobile Number" onClose={handleClose} />

      <Drawer.Content>
        <form onSubmit={handleSubmit}>
          <BlacklistedMobileNumberFormFields
            mobileNumber={formData.mobile_number}
            reason={formData.reason}
            active={formData.active}
            onMobileNumberChange={handleMobileNumberChange}
            onReasonChange={handleReasonChange}
            onActiveChange={handleActiveChange}
            onPhoneValidationChange={handlePhoneValidationChange}
            showAlert
          />
        </form>
      </Drawer.Content>

      <Drawer.Footer fullWidth>
        <Drawer.PrimaryButton
          type="submit"
          onClick={handleSubmit}
          disabled={!isValid || loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Drawer.PrimaryButton>
      </Drawer.Footer>
    </Drawer>
  );
};

export default AddBlacklistedMobileNumberForm;